// Moving an email into a folder: the drag payload plus a tiny window
// event so the three places involved (message list rows, the folder
// sidebar, the reading pane's Move button) stay decoupled.
//
// DECISION: a window CustomEvent rather than lifting state into a shared
// context. The sidebar mounts on every OS page while the message list
// only exists on /email, and the list simply ignores events when it is
// not mounted. A context would force both into one provider for what is
// a fire-and-forget "this row left the folder" notice.

export const EMAIL_DRAG_MIME = "application/x-stewardship-email";

export interface EmailDragPayload {
  messageId: string;
  /** URL key of the folder the message is being dragged out of. */
  sourceFolderKey: string;
}

export function readEmailDragPayload(dt: DataTransfer | null): EmailDragPayload | null {
  if (!dt) return null;
  try {
    const raw = dt.getData(EMAIL_DRAG_MIME);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<EmailDragPayload>;
    if (typeof parsed.messageId !== "string" || typeof parsed.sourceFolderKey !== "string") {
      return null;
    }
    return { messageId: parsed.messageId, sourceFolderKey: parsed.sourceFolderKey };
  } catch {
    return null;
  }
}

/** True while an email (not a file or a folder-prefs row) is mid-drag.
 *  getData() is blocked during dragover, but `types` is readable. */
export function isEmailDrag(dt: DataTransfer | null): boolean {
  return !!dt && Array.from(dt.types).includes(EMAIL_DRAG_MIME);
}

export type EmailMoveEvent =
  | { type: "moved"; messageId: string; sourceFolderKey: string | null }
  | { type: "restored"; sourceFolderKey: string };

const EVENT_NAME = "stewardship:email-moved";

export function emitEmailMove(detail: EmailMoveEvent): void {
  window.dispatchEvent(new CustomEvent<EmailMoveEvent>(EVENT_NAME, { detail }));
}

export function subscribeEmailMove(handler: (e: EmailMoveEvent) => void): () => void {
  const listener = (ev: Event) => handler((ev as CustomEvent<EmailMoveEvent>).detail);
  window.addEventListener(EVENT_NAME, listener);
  return () => window.removeEventListener(EVENT_NAME, listener);
}
